import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { 
  Firestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import {
  Auth,
  User,
  onAuthStateChanged,
  signInAnonymously,
  browserSessionPersistence,
  setPersistence
} from 'firebase/auth';
import { calculateEffectiveSubscription, migrateLegacySubscription } from '../utils/subscriptionCalculator';
import { ensureDefaultTeamExists, addUserToTeam, getUserCurrentTeam } from '../utils/teamManager';
import logger from '../utils/logger';
import { createSessionReader } from '../utils/sessionReads';

// Enhanced user interface supporting multi-source subscriptions
interface JuggleUser {
  id: string;
  displayName: string;
  email?: string;
  role?: string[]; // Array of roles: ['admin'], ['admin_org_123'], ['admin_team_456'], etc.
  totalJuggles: number;
  longestSession: number;
  bestSessionCount: number;
  bestRate: number;
  joinDate: Date;
  
  // Legacy fields (maintained for backward compatibility)
  isPremium: boolean;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  billingProvider?: 'stripe' | 'apple' | 'admin' | 'legacy';
  stripeSubscriptionId?: string;
  appleReceiptData?: string;
  
  // New multi-source subscription system
  individual?: {
    subscriptionTier: string;
    subscriptionStatus: string;
    billingProvider: 'stripe' | 'apple' | 'admin' | 'legacy';
    stripeSubscriptionId?: string;
    periodEnd?: Date;
  };
  organization?: {
    organizationId: string;
    teamId?: string;
    allocatedSeat?: {
      tier: string;
      assignedAt: Date;
      assignedBy: string;
      status: 'active' | 'revoked' | 'pending';
    };
  };
  effective?: {
    tier: string;
    source: 'individual' | 'organization' | 'admin' | 'legacy' | 'default';
    lastCalculated: Date;
  };
  
  // Current team membership
  currentTeam?: {
    teamId: string;
    teamName?: string;
    role: 'owner' | 'admin' | 'member';
    joinedAt: Date;
  };
}

interface JuggleSession {
  id: string;
  userId: string;
  juggleCount: number;
  duration: number;
  date: Date;
  mode: 'manual' | 'ai';
  juggleRate: number;
}

interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  value: number;
  type: 'totalJuggles' | 'bestSessionCount' | 'longestSession' | 'bestRate';
}

type LeaderboardType = 'totalJuggles' | 'bestSessionCount' | 'longestSession' | 'bestRate';

interface FirebaseContextType {
  db: Firestore;
  auth: Auth;
  user: User | null;
  currentUser: JuggleUser | null;
  loading: boolean;
  
  // Data fetching functions
  fetchLeaderboard: (type: LeaderboardType, limitCount?: number) => Promise<LeaderboardEntry[]>;
  fetchUserProfile: (userId: string) => Promise<JuggleUser | null>;
  fetchUserSessions: (userId: string, limitCount?: number) => Promise<JuggleSession[]>;
  signInAnonymous: () => Promise<void>;
}

const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

interface FirebaseProviderProps {
  children: React.ReactNode;
  db: Firestore;
  auth: Auth;
  user?: User | null; // Allow passing user from parent
  loading?: boolean; // Allow passing loading state from parent
}

export const FirebaseProvider: React.FC<FirebaseProviderProps> = ({ 
  children, 
  db, 
  auth, 
  user: propUser, 
  loading: propLoading 
}) => {
  const [internalUser, setInternalUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<JuggleUser | null>(null);
  const [internalLoading, setInternalLoading] = useState(true);

  // Use passed user/loading state if available, otherwise manage internally
  const user = propUser !== undefined ? propUser : internalUser;
  const loading = propLoading !== undefined ? propLoading : internalLoading;

  useEffect(() => {
    // Configure Firebase Auth persistence for session-based authentication
    setPersistence(auth, browserSessionPersistence)
      .then(() => {
        logger.log('✅ FirebaseContext: Auth persistence set to session-based');
      })
      .catch(error => {
        logger.error('❌ FirebaseContext: Persistence setup failed:', error);
      });
      
    // Only set up auth listener if user is not being passed from parent
    if (propUser === undefined) {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        logger.log('🔥 FirebaseContext: Auth state changed:', firebaseUser?.email || 'null');
        setInternalUser(firebaseUser);
        
        if (firebaseUser) {
          try {
            let userProfile = await fetchUserProfile(firebaseUser.uid);
            
            // If user profile doesn't exist, create one
            if (!userProfile) {
              logger.log('🆕 Creating new user profile for:', firebaseUser.email);
              const newUserData = {
                email: firebaseUser.email || '',
                displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
                totalJuggles: 0,
                longestSession: 0,
                bestSessionCount: 0,
                bestRate: 0,
                joinDate: new Date(),
                // Legacy fields
                isPremium: false,
                subscriptionTier: 'free',
                billingProvider: undefined,
                uid: firebaseUser.uid,
                // New effective subscription calculation will show 'free' by default
              };
              
              await setDoc(doc(db, 'users', firebaseUser.uid), newUserData);
              logger.log('✅ User profile created successfully');
              
              // Add user to specific default team (one-time on account creation)
              try {
                const defaultTeamId = 'MtGe3C8Z1vx9JdgtJlSh';
                await addUserToTeam(
                  db,
                  defaultTeamId,
                  firebaseUser.uid,
                  firebaseUser.email || '',
                  'member',
                  'system'
                );
                logger.log('✅ User added to default team:', defaultTeamId);
              } catch (teamError) {
                logger.warn('⚠️ Could not add user to default team:', teamError);
                // Don't block user creation if team assignment fails
              }
              
              // Fetch the newly created profile
              userProfile = await fetchUserProfile(firebaseUser.uid);
            }
            
            setCurrentUser(userProfile);
          } catch (error) {
            logger.error('Error fetching/creating user profile:', error);
          }
        } else {
          setCurrentUser(null);
        }
        
        setInternalLoading(false);
      });

      return unsubscribe;
    } else {
      // If user is passed from parent, update currentUser when it changes
      if (user) {
        // Apply the same user creation logic as in onAuthStateChanged
        const handleExternalUser = async () => {
          try {
            let userProfile = await fetchUserProfile(user.uid);
            
            // If user profile doesn't exist, create one
            if (!userProfile) {
              logger.log('🆕 Creating new user profile for:', user.email);
              const newUserData = {
                email: user.email || '',
                displayName: user.displayName || user.email?.split('@')[0] || 'User',
                totalJuggles: 0,
                longestSession: 0,
                bestSessionCount: 0,
                bestRate: 0,
                joinDate: new Date(),
                isPremium: false,
                subscriptionTier: 'free',
                billingProvider: undefined, // Will be set when they subscribe
                uid: user.uid
              };
              
              await setDoc(doc(db, 'users', user.uid), newUserData);
              logger.log('✅ User profile created successfully');
              
              // Add user to specific default team (one-time on account creation)
              try {
                const defaultTeamId = 'MtGe3C8Z1vx9JdgtJlSh';
                await addUserToTeam(
                  db,
                  defaultTeamId,
                  user.uid,
                  user.email || '',
                  'member',
                  'system'
                );
                logger.log('✅ User added to default team:', defaultTeamId);
              } catch (teamError) {
                logger.warn('⚠️ Could not add user to default team:', teamError);
                // Don't block user creation if team assignment fails
              }
              
              // Fetch the newly created profile
              userProfile = await fetchUserProfile(user.uid);
            }
            
            setCurrentUser(userProfile);
          } catch (error) {
            logger.error('Error fetching/creating user profile:', error);
          }
        };
        
        handleExternalUser();
      } else {
        setCurrentUser(null);
      }
    }
  }, [auth, propUser, user]);

  const fetchLeaderboard = async (type: LeaderboardType, limitCount = 50): Promise<LeaderboardEntry[]> => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        orderBy(type, 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const entries: LeaderboardEntry[] = [];
      
      snapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        const user = {
          id: doc.id,
          displayName: data.displayName || 'Anonymous',
          email: data.email,
          totalJuggles: data.totalJuggles || 0,
          longestSession: data.longestSession || 0,
          bestSessionCount: data.bestSessionCount || 0,
          bestRate: data.bestRate || 0,
          joinDate: data.joinDate?.toDate() || new Date(),
          isPremium: data.isPremium || false
        };
        
        let value: number;
        switch (type) {
          case 'totalJuggles':
            value = user.totalJuggles;
            break;
          case 'bestSessionCount':
            value = user.bestSessionCount;
            break;
          case 'longestSession':
            value = user.longestSession;
            break;
          case 'bestRate':
            value = user.bestRate;
            break;
        }
        
        entries.push({
          rank: index + 1,
          userId: user.id,
          displayName: user.displayName,
          value,
          type
        });
      });
      
      return entries;
    } catch (error) {
      logger.error('Error fetching leaderboard:', error);
      return [];
    }
  };

  const fetchUserProfile = async (userId: string): Promise<JuggleUser | null> => {
    try {
      const userDoc = doc(db, 'users', userId);
      const docSnap = await getDoc(userDoc);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Migrate legacy subscription data if needed
        const migratedData = migrateLegacySubscription(data);
        
        // Calculate effective subscription
        const effectiveSubscription = calculateEffectiveSubscription(migratedData);
        
        // Get current team membership from memberIds arrays
        let currentTeam: JuggleUser['currentTeam'] = undefined;
        try {
          const currentTeamId = await getUserCurrentTeam(db, userId);
          if (currentTeamId) {
            // Try teams collection first, then organizations
            let teamDoc = null;
            let teamData = null;
            
            const teamRef = doc(db, 'teams', currentTeamId);
            teamDoc = await getDoc(teamRef);
            if (teamDoc.exists()) {
              teamData = teamDoc.data();
            } else {
              const orgRef = doc(db, 'organizations', currentTeamId);
              teamDoc = await getDoc(orgRef);
              if (teamDoc.exists()) {
                teamData = teamDoc.data();
              }
            }
            
            if (teamData && teamData.memberIds && teamData.memberIds.includes(userId)) {
              // Determine user role from adminIds array or ownerId
              let role: 'owner' | 'admin' | 'member' = 'member';
              if (teamData.ownerId === userId) {
                role = 'owner';
              } else if (teamData.adminIds && teamData.adminIds.includes(userId)) {
                role = 'admin';
              }
              
              currentTeam = {
                teamId: currentTeamId,
                teamName: teamData.name,
                role,
                joinedAt: teamData.createdAt?.toDate() || new Date()
              };
            }
          }
        } catch (teamError) {
          logger.warn('Could not fetch team info for user:', userId, teamError);
        }
        
        logger.log(`👤 Loading user profile for ${userId}:`, {
          email: data.email,
          role: data.role,
          displayName: data.displayName
        });

        // Handle role array migration
        let userRoles: string[] = [];
        if (Array.isArray(data.role)) {
          userRoles = data.role;
        } else if (data.role === 'admin') {
          userRoles = ['admin'];
        } else if (data.role === 'team_admin') {
          userRoles = ['team_admin']; // Will be migrated to specific team roles later
        } else {
          userRoles = ['user'];
        }

        const userProfile: JuggleUser = {
          id: userId,
          displayName: data.displayName || 'Anonymous',
          email: data.email,
          role: userRoles,
          totalJuggles: data.totalJuggles || 0,
          longestSession: data.longestSession || 0,
          bestSessionCount: data.bestSessionCount || 0,
          bestRate: data.bestRate || 0,
          joinDate: data.joinDate?.toDate() || new Date(),
          
          // Legacy fields (maintained for backward compatibility)
          isPremium: effectiveSubscription.tier !== 'free',
          subscriptionTier: effectiveSubscription.tier,
          subscriptionStatus: data.subscriptionStatus,
          billingProvider: data.billingProvider || (data.isPremium ? 'legacy' : undefined),
          stripeSubscriptionId: data.stripeSubscriptionId,
          appleReceiptData: data.appleReceiptData,
          
          // New multi-source subscription fields
          individual: data.individual ? {
            subscriptionTier: data.individual.subscriptionTier,
            subscriptionStatus: data.individual.subscriptionStatus,
            billingProvider: data.individual.billingProvider,
            stripeSubscriptionId: data.individual.stripeSubscriptionId,
            periodEnd: data.individual.periodEnd?.toDate()
          } : undefined,
          
          organization: data.organization ? {
            organizationId: data.organization.organizationId,
            teamId: data.organization.teamId,
            allocatedSeat: data.organization.allocatedSeat ? {
              tier: data.organization.allocatedSeat.tier,
              assignedAt: data.organization.allocatedSeat.assignedAt?.toDate() || new Date(),
              assignedBy: data.organization.allocatedSeat.assignedBy,
              status: data.organization.allocatedSeat.status
            } : undefined
          } : undefined,
          
          effective: {
            tier: effectiveSubscription.tier,
            source: effectiveSubscription.source as 'individual' | 'organization' | 'admin' | 'legacy' | 'default',
            lastCalculated: effectiveSubscription.lastCalculated
          },
          
          currentTeam
        };
        
        return userProfile;
      }
      
      return null;
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      return null;
    }
  };

  const fetchUserSessions = useMemo(() => createSessionReader(db), [db, user]);

  const signInAnonymous = async (): Promise<void> => {
    try {
      await signInAnonymously(auth);
    } catch (error) {
      logger.error('Error signing in anonymously:', error);
      throw error;
    }
  };

  const value: FirebaseContextType = {
    db,
    auth,
    user,
    currentUser,
    loading,
    fetchLeaderboard,
    fetchUserProfile,
    fetchUserSessions,
    signInAnonymous
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};